import type { AgentConfig } from './types';
import { ChatMenu, type ConversationSummary } from './chat-menu';

interface ChatHeaderProps {
  agent: AgentConfig;
  /**
   * Called when the user picks "Restart conversation" from the header menu.
   * When provided, the kebab menu is interactive. Hide-by-omission keeps the
   * header stable for embedders that manage their own session (agent test panel).
   */
  onRestart?: () => void;
  /** Menu label for the restart item (i18n-friendly). */
  restartLabel?: string;
  /** Past conversations for the "Previous conversations" section (lazy-loaded). */
  conversations?: ConversationSummary[];
  conversationsLoading?: boolean;
  currentConversationId?: string | null;
  /** Fired when the menu opens so the parent can lazy-load `conversations`. */
  onOpenMenu?: () => void;
  onSelectConversation?: (id: string) => void;
  previousLabel?: string;
  emptyConversationsLabel?: string;
}

export function ChatHeader({
  agent,
  onRestart,
  restartLabel,
  conversations,
  conversationsLoading,
  currentConversationId,
  onOpenMenu,
  onSelectConversation,
  previousLabel,
  emptyConversationsLabel,
}: ChatHeaderProps) {
  return (
    <header className="relative flex shrink-0 items-center gap-3.5 border-b border-border bg-card px-6 py-4 dark:border-white/[0.08] dark:bg-transparent">
      {/* Avatar */}
      <div className="relative flex size-12 shrink-0 items-center justify-center rounded-full bg-primary text-base font-bold text-primary-foreground">
        {agent.avatarUrl ? (
          <img
            src={agent.avatarUrl}
            alt={agent.name}
            className="size-12 rounded-full object-cover"
          />
        ) : (
          agent.initials
        )}
      </div>

      {/* Name + subtitle */}
      <div className="min-w-0 flex-1">
        <p className="text-base font-bold leading-tight text-foreground dark:text-white">
          {agent.name}
        </p>
        {agent.subtitle && (
          <p className="text-sm font-medium text-muted-foreground dark:text-white/50">
            {agent.subtitle}
          </p>
        )}
      </div>

      {/* Kebab menu — restart + previous conversations. Only interactive when a
          restart handler is provided; otherwise render nothing (no dead button). */}
      {onRestart && (
        <ChatMenu
          onRestart={onRestart}
          restartLabel={restartLabel ?? 'Restart conversation'}
          conversations={conversations}
          conversationsLoading={conversationsLoading}
          currentConversationId={currentConversationId}
          onOpen={onOpenMenu}
          onSelectConversation={onSelectConversation}
          previousLabel={previousLabel}
          emptyLabel={emptyConversationsLabel}
        />
      )}
    </header>
  );
}
